import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileText, RefreshCw, Loader2, Clock, CheckCircle2, AlertCircle, Save, Send, ExternalLink, Plus, Trash2, Link2, Copy, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_META = {
  awaiting_intake:    { label: "Awaiting intake",   tone: "bg-[#ECF0F4] text-[#6F8FA8]" },
  intake_submitted:   { label: "Drafting",          tone: "bg-[#FDF4E4] text-[#8B6914]" },
  draft_ready:        { label: "Ready for review",  tone: "bg-[#C6D21F] text-[#0D2F4D]" },
  in_review:          { label: "In review",         tone: "bg-[#DAE7F0] text-[#0D2F4D]" },
  approved_sent:      { label: "Sent",              tone: "bg-[#0D2F4D] text-[#C6D21F]" },
};

const EMPTY_DRAFT = {
  summary: "", target_role: "", skills: [], experience: [], education: [], certifications: [],
  job_ad_keywords_used: [], notes_for_polish: "",
};

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

function pastDue(dueAt) {
  if (!dueAt) return false;
  try { return new Date(dueAt).getTime() < Date.now(); }
  catch { return false; }
}

export const ResumeReviewsTab = ({ pw }) => {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [dirty, setDirty] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/resume-reviews`, { headers: { "X-Admin-Password": pw } });
      setItems(r.data?.reviews || []);
      setCounts(r.data?.counts || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load resume reviews.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openDetail = async (id) => {
    if (dirty && !window.confirm("You have unsaved edits — leave anyway?")) return;
    setSelected(id); setDetail(null); setDirty(false);
    try {
      const r = await axios.get(`${API}/admin/resume-reviews/${id}`, { headers: { "X-Admin-Password": pw } });
      setDetail(r.data);
      setDraft({ ...EMPTY_DRAFT, ...(r.data.draft || {}) });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load review."); }
  };

  const regenerate = async (id) => {
    if (dirty && !window.confirm("Regenerating will discard your unsaved edits. Continue?")) return;
    if (!window.confirm("Re-run the AI drafting on this intake? Overwrites the current draft.")) return;
    setRegenerating(true);
    try {
      const r = await axios.post(`${API}/admin/resume-reviews/${id}/regenerate`, {}, { headers: { "X-Admin-Password": pw } });
      setDetail((d) => ({ ...d, draft: r.data.draft, stage: "draft_ready" }));
      setDraft({ ...EMPTY_DRAFT, ...(r.data.draft || {}) });
      setDirty(false);
      toast.success("Draft regenerated.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Regenerate failed."); }
    finally { setRegenerating(false); }
  };

  const saveDraft = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/resume-reviews/${selected}`, { draft }, { headers: { "X-Admin-Password": pw } });
      setDirty(false);
      toast.success("Draft saved.");
      setDetail((d) => (d ? { ...d, draft, stage: "in_review" } : d));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
    finally { setSaving(false); }
  };

  const approveAndSend = async () => {
    if (!selected) return;
    if (!window.confirm("Approve this CV and send the download link to the client? This can't be undone.")) return;
    setSending(true);
    try {
      const r = await axios.post(
        `${API}/admin/resume-reviews/${selected}/approve`,
        { draft },
        { headers: { "X-Admin-Password": pw } },
      );
      toast.success("Sent — client will receive the download link within a minute.");
      setDirty(false);
      setDetail((d) => d ? ({
        ...d, stage: "approved_sent", draft,
        download_token: r.data.download_url?.split("/").pop(),
        download_expires_at: r.data.download_expires_at,
      }) : d);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to send."); }
    finally { setSending(false); }
  };

  const setDraftField = (key, value) => { setDraft((d) => ({ ...d, [key]: value })); setDirty(true); };
  const setExpItem = (i, key, value) => {
    setDraft((d) => {
      const exp = [...(d.experience || [])];
      exp[i] = { ...(exp[i] || {}), [key]: value };
      return { ...d, experience: exp };
    });
    setDirty(true);
  };
  const setExpBullets = (i, textareaValue) => {
    const bullets = textareaValue.split("\n").filter((b) => b.trim());
    setExpItem(i, "bullets", bullets);
  };
  const addExp = () => {
    setDraft((d) => ({ ...d, experience: [...(d.experience || []), { role: "", company: "", location: "", start: "", end: "", bullets: [] }] }));
    setDirty(true);
  };
  const removeExp = (i) => {
    setDraft((d) => ({ ...d, experience: (d.experience || []).filter((_, idx) => idx !== i) }));
    setDirty(true);
  };
  const setEduItem = (i, key, value) => {
    setDraft((d) => {
      const edu = [...(d.education || [])];
      edu[i] = { ...(edu[i] || {}), [key]: value };
      return { ...d, education: edu };
    });
    setDirty(true);
  };
  const addEdu = () => { setDraft((d) => ({ ...d, education: [...(d.education || []), { qualification: "", institution: "", year: "" }] })); setDirty(true); };
  const removeEdu = (i) => { setDraft((d) => ({ ...d, education: (d.education || []).filter((_, idx) => idx !== i) })); setDirty(true); };

  // Manual "send CV Builder link" flow
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkForm, setLinkForm] = useState({ email: "", first_name: "", last_name: "", note: "" });
  const [linkResult, setLinkResult] = useState(null);
  const [linkSubmitting, setLinkSubmitting] = useState(false);

  const submitManualLink = async () => {
    if (!linkForm.email) { toast.error("Email is required."); return; }
    setLinkSubmitting(true);
    try {
      const r = await axios.post(`${API}/admin/resume-reviews/manual-link`, linkForm, { headers: { "X-Admin-Password": pw } });
      setLinkResult(r.data);
      toast.success(r.data.reused_existing ? "Existing intake link retrieved." : "Fresh CV Builder link ready.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create link."); }
    finally { setLinkSubmitting(false); }
  };

  const copyText = (text, label = "Copied") => {
    navigator.clipboard?.writeText(text);
    toast.success(label);
  };

  const resetLinkModal = () => {
    setShowLinkModal(false);
    setLinkForm({ email: "", first_name: "", last_name: "", note: "" });
    setLinkResult(null);
  };

  return (
    <div data-testid="resume-reviews-tab">
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <FileText size={22} className="text-[#0D2F4D]" />
        <h2 className="font-display text-2xl font-extrabold text-[#0D2F4D]">Resume Reviews</h2>
        <Badge variant="secondary" className="bg-[#ECF0F4] text-[#6F8FA8] font-bold text-xs uppercase tracking-widest">
          {items.length}
        </Badge>
        <div className="ml-auto flex items-center gap-2">
          <Button
            onClick={() => setShowLinkModal(true)}
            data-testid="resume-send-manual-link"
            size="sm"
            className="bg-[#0D2F4D] text-white hover:bg-[#1a4063] rounded-full"
          >
            <Link2 size={14} /> Send CV Builder link
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={load}
            data-testid="resume-refresh"
            className="text-[#0D2F4D] hover:text-[#C6D21F] hover:bg-transparent"
          >
            <RefreshCw size={14} /> Refresh
          </Button>
        </div>
      </div>

      {/* Stage counts */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-6">
        {Object.entries(STAGE_META).map(([k, m]) => (
          <Card key={k} className="border-[#E4E7EA]">
            <CardContent className="p-3 text-center">
              <Badge className={`text-[10px] uppercase tracking-widest font-bold ${m.tone} hover:${m.tone}`}>
                {m.label}
              </Badge>
              <p className="text-2xl font-display font-extrabold text-[#0D2F4D] mt-1">{counts[k] || 0}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {loading ? (
        <p className="text-center text-[#6F8FA8] py-12">Loading…</p>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-[#E4E7EA]">
          <p className="text-[#6F8FA8]">No resume reviews yet. When someone buys the $207 Resume Service, they'll appear here.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-[380px_1fr] gap-4">
          {/* List */}
          <ul className="bg-white rounded-2xl border border-[#E4E7EA] max-h-[600px] overflow-y-auto divide-y divide-[#ECF0F4]">
            {items.map((r) => {
              const overdue = pastDue(r.due_at) && r.stage !== "approved_sent";
              return (
                <li key={r.id}>
                  <button onClick={() => openDetail(r.id)}
                    data-testid={`resume-row-${r.id}`}
                    className={`w-full text-left p-4 hover:bg-[#F8F9FB] transition-colors ${selected === r.id ? "bg-[#F8F9FB]" : ""}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${STAGE_META[r.stage]?.tone || "bg-[#ECF0F4] text-[#6F8FA8]"}`}>
                        {STAGE_META[r.stage]?.label || r.stage}
                      </span>
                      {overdue && <span className="text-[10px] font-bold text-[#B91C1C] inline-flex items-center gap-1"><AlertCircle size={10} /> OVERDUE</span>}
                    </div>
                    <p className="text-sm font-semibold text-[#0D2F4D]">
                      {r.first_name} {r.last_name || ""} · {r.email}
                    </p>
                    <p className="text-xs text-[#6F8FA8] mt-1">
                      {(r.intake?.target_job_title) || "—"} · Due {fmt(r.due_at)}
                    </p>
                  </button>
                </li>
              );
            })}
          </ul>

          {/* Detail */}
          <div className="bg-white rounded-2xl border border-[#E4E7EA] p-5 md:p-6 min-h-[400px]" data-testid="resume-detail">
            {!selected ? (
              <p className="text-sm text-[#6F8FA8] text-center py-24">Pick a review from the list to open.</p>
            ) : !detail ? (
              <div className="text-center py-24"><Loader2 className="animate-spin mx-auto text-[#0D2F4D]" size={24} /></div>
            ) : (
              <div className="space-y-5">
                <div>
                  <p className="text-xs uppercase tracking-widest font-bold text-[#6F8FA8]">Client</p>
                  <p className="font-display text-xl font-extrabold text-[#0D2F4D]">
                    {detail.first_name} {detail.last_name || ""}
                  </p>
                  <p className="text-sm text-[#6F8FA8]">{detail.email}</p>
                </div>

                <div className="grid grid-cols-2 gap-3 text-xs">
                  <Stat label="Stage" value={STAGE_META[detail.stage]?.label || detail.stage} />
                  <Stat label="Amount" value={`$${(detail.amount_paid || 0).toFixed(2)}`} />
                  <Stat label="Purchased" value={fmt(detail.purchased_at)} />
                  <Stat label="Due" value={fmt(detail.due_at)} />
                </div>

                {detail.intake && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs uppercase tracking-widest font-bold text-[#6F8FA8]">Intake</p>
                      <div className="flex gap-1">
                        <button
                          onClick={async () => {
                            try {
                              const r = await axios.get(`${API}/admin/resume-reviews/${detail.id}/export?format=markdown`, {
                                headers: { "X-Admin-Password": pw },
                                responseType: "blob",
                              });
                              const url = URL.createObjectURL(r.data);
                              const a = document.createElement("a");
                              a.href = url;
                              // Try to derive filename from response header
                              const cd = r.headers["content-disposition"] || "";
                              const m = cd.match(/filename="([^"]+)"/);
                              a.download = m ? m[1] : `cv-intake-${detail.id.slice(0,8)}.md`;
                              document.body.appendChild(a); a.click(); a.remove();
                              URL.revokeObjectURL(url);
                              toast.success("Intake downloaded");
                            } catch (e) { toast.error(`Download failed: ${e?.response?.data?.detail || e.message}`); }
                          }}
                          className="text-[10px] inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#F7F9E8] text-[#0D2F4D] border border-[#C6D21F]/40 hover:bg-[#C6D21F]/20"
                          data-testid="intake-download-md"
                          title="Download intake as Markdown — paste into Word / Docs / Canva"
                        ><Download size={11}/>Markdown</button>
                        <button
                          onClick={async () => {
                            try {
                              const r = await axios.get(`${API}/admin/resume-reviews/${detail.id}/export?format=json`, {
                                headers: { "X-Admin-Password": pw },
                                responseType: "blob",
                              });
                              const url = URL.createObjectURL(r.data);
                              const a = document.createElement("a");
                              a.href = url; a.download = `cv-intake-${detail.id.slice(0,8)}.json`;
                              document.body.appendChild(a); a.click(); a.remove();
                              URL.revokeObjectURL(url);
                              toast.success("Intake JSON downloaded");
                            } catch (e) { toast.error(`Download failed: ${e?.response?.data?.detail || e.message}`); }
                          }}
                          className="text-[10px] inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#ECF0F4] text-[#0D2F4D] border border-[#D6DCE3] hover:bg-[#DCE4EA]"
                          data-testid="intake-download-json"
                          title="Download intake as JSON — structured export"
                        ><Download size={11}/>JSON</button>
                      </div>
                    </div>
                    <div className="text-sm bg-[#F8F9FB] rounded-lg p-4 space-y-2">
                      <p><b>Target role:</b> {detail.intake.target_job_title}</p>
                      <p><b>Industry:</b> {detail.intake.industry}</p>
                      {detail.intake.linkedin_url && <p><b>LinkedIn:</b> <a className="underline text-[#0D2F4D]" href={detail.intake.linkedin_url} target="_blank" rel="noreferrer">{detail.intake.linkedin_url}</a></p>}
                      {detail.intake.new_roles && <p className="whitespace-pre-line"><b>New roles:</b> {detail.intake.new_roles}</p>}
                      {detail.intake.achievements && <p className="whitespace-pre-line"><b>Achievements:</b> {detail.intake.achievements}</p>}
                      {detail.intake.qualifications && <p className="whitespace-pre-line"><b>Quals:</b> {detail.intake.qualifications}</p>}
                      {detail.intake.job_ad_text && (
                        <details><summary className="cursor-pointer font-bold">Job ad</summary>
                          <p className="whitespace-pre-line mt-2 text-xs text-[#6F8FA8]">{detail.intake.job_ad_text}</p>
                        </details>
                      )}
                    </div>
                  </div>
                )}

                {/* Files */}
                <div>
                  <p className="text-xs uppercase tracking-widest font-bold text-[#6F8FA8] mb-2">Uploaded files</p>
                  <div className="flex gap-2 flex-wrap text-xs">
                    {Object.entries(detail.files || {}).map(([k, v]) => (
                      <span key={k} className={`px-3 py-1 rounded-full ${v ? "bg-[#C6D21F] text-[#0D2F4D]" : "bg-[#ECF0F4] text-[#6F8FA8]"}`}>
                        {v ? <CheckCircle2 size={11} className="inline mr-1" /> : null}
                        {k.replace("_", " ")}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Draft — either inline editor or "not yet" placeholder */}
                {detail.draft ? (
                  <div className="pt-2 border-t border-[#ECF0F4]" data-testid="resume-editor">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-xs uppercase tracking-widest font-bold text-[#6F8FA8]">
                        Editable draft {dirty && <span className="ml-2 text-[#B91C1C] normal-case font-normal">· unsaved</span>}
                      </p>
                      <div className="flex items-center gap-3">
                        <button onClick={() => regenerate(detail.id)} disabled={regenerating}
                          data-testid="resume-regenerate"
                          className="text-xs inline-flex items-center gap-1 text-[#6F8FA8] hover:text-[#B91C1C]">
                          <RefreshCw size={12} className={regenerating ? "animate-spin" : ""} /> {regenerating ? "Regenerating…" : "Regenerate"}
                        </button>
                      </div>
                    </div>

                    {/* Approved / sent state */}
                    {detail.manual_cv_sent && !detail.download_token && (
                      <div
                        className="mb-4 bg-[#EFF6FF] border border-[#93C5FD] rounded-lg p-3 text-sm text-[#0D2F4D] flex items-start gap-3"
                        data-testid="resume-manual-sent-banner"
                      >
                        <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
                        <div className="flex-1">
                          <p className="font-bold">Manually sent by admin</p>
                          <p className="text-xs text-[#33475F] mt-0.5">
                            Marked as delivered outside the standard flow{detail.manual_cv_sent_by ? ` by ${detail.manual_cv_sent_by}` : ""}
                            {detail.manual_cv_sent_at ? ` on ${fmt(detail.manual_cv_sent_at)}` : ""}.
                            {detail.manual_cv_sent_note ? <><br/><span className="italic">"{detail.manual_cv_sent_note}"</span></> : null}
                          </p>
                        </div>
                        <button
                          onClick={async () => {
                            if (!window.confirm("Undo the manual-sent flag? The review will pop back onto the queue.")) return;
                            try {
                              await axios.post(`${API}/admin/resume-reviews/${detail.id}/manual-sent/undo`, {},
                                { headers: { "X-Admin-Password": pw } });
                              toast.success("Manual-sent flag cleared.");
                              await openDetail(detail.id); load();
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || e.message);
                            }
                          }}
                          className="text-[10px] font-bold uppercase tracking-widest text-[#B91C1C] hover:underline"
                          data-testid="resume-manual-sent-undo"
                        >
                          Undo
                        </button>
                      </div>
                    )}

                    {/* Approved / sent state */}
                    {detail.stage === "approved_sent" && detail.download_token && (
                      <div className="mb-4 bg-[#C6D21F]/20 border border-[#C6D21F] rounded-lg p-3 text-sm text-[#0D2F4D] space-y-2" data-testid="resume-sent-banner">
                        <p className="font-bold flex items-center gap-2"><CheckCircle2 size={14} /> Sent — the download link has been emailed.</p>

                        {/* Download analytics */}
                        <div className="grid grid-cols-3 gap-2 text-xs" data-testid="resume-download-analytics">
                          <MetricPill
                            testid="metric-download-count"
                            label="Downloads"
                            value={detail.download_count || 0}
                          />
                          <MetricPill
                            testid="metric-first-opened"
                            label="First opened"
                            value={detail.first_downloaded_at ? fmt(detail.first_downloaded_at) : "—"}
                          />
                          <MetricPill
                            testid="metric-unique-ips"
                            label="Unique IPs"
                            value={(detail.unique_ips || []).length}
                          />
                        </div>
                        {detail.last_downloaded_at && (
                          <p className="text-[10px] text-[#6F8FA8]">
                            Last opened {fmt(detail.last_downloaded_at)}
                          </p>
                        )}
                        {!detail.first_downloaded_at && (
                          <p className="text-[10px] text-[#B91C1C] italic">
                            Client hasn't opened the file yet — worth a nudge in a couple of days.
                          </p>
                        )}

                        <a href={`${API}/resume-review/download/${detail.download_token}`} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 underline text-xs">
                          <ExternalLink size={11} /> Copy of the client's PDF
                        </a>
                        {detail.download_expires_at && <p className="text-xs text-[#6F8FA8]">Expires {fmt(detail.download_expires_at)}</p>}
                      </div>
                    )}

                    <Field label="Professional summary">
                      <textarea rows={4} value={draft.summary || ""} onChange={(e) => setDraftField("summary", e.target.value)}
                        data-testid="editor-summary"
                        className="w-full rounded-lg border border-[#D6DCE3] px-3 py-2 text-sm focus:border-[#0D2F4D] outline-none" />
                    </Field>

                    <Field label="Target role">
                      <input type="text" value={draft.target_role || ""} onChange={(e) => setDraftField("target_role", e.target.value)}
                        data-testid="editor-target-role"
                        className="w-full rounded-lg border border-[#D6DCE3] px-3 py-2 text-sm focus:border-[#0D2F4D] outline-none" />
                    </Field>

                    <Field label="Skills (one per line)">
                      <textarea rows={4} value={(draft.skills || []).join("\n")}
                        onChange={(e) => setDraftField("skills", e.target.value.split("\n").filter((s) => s.trim()))}
                        data-testid="editor-skills"
                        className="w-full rounded-lg border border-[#D6DCE3] px-3 py-2 text-sm focus:border-[#0D2F4D] outline-none" />
                    </Field>

                    <Field label="Experience">
                      <div className="space-y-3">
                        {(draft.experience || []).map((job, i) => (
                          <div key={i} className="rounded-lg border border-[#D6DCE3] p-3 space-y-2" data-testid={`editor-exp-${i}`}>
                            <div className="flex justify-between">
                              <p className="text-[10px] font-bold uppercase text-[#6F8FA8]">Role {i + 1}</p>
                              <button onClick={() => removeExp(i)} className="text-[#B91C1C] hover:opacity-80" title="Remove">
                                <Trash2 size={12} />
                              </button>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <input placeholder="Role" value={job.role || ""} onChange={(e) => setExpItem(i, "role", e.target.value)}
                                className="rounded-md border border-[#D6DCE3] px-2 py-1.5 text-sm" />
                              <input placeholder="Company" value={job.company || ""} onChange={(e) => setExpItem(i, "company", e.target.value)}
                                className="rounded-md border border-[#D6DCE3] px-2 py-1.5 text-sm" />
                              <input placeholder="Location" value={job.location || ""} onChange={(e) => setExpItem(i, "location", e.target.value)}
                                className="rounded-md border border-[#D6DCE3] px-2 py-1.5 text-sm" />
                              <div className="grid grid-cols-2 gap-2">
                                <input placeholder="Start (MM/YYYY)" value={job.start || ""} onChange={(e) => setExpItem(i, "start", e.target.value)}
                                  className="rounded-md border border-[#D6DCE3] px-2 py-1.5 text-sm" />
                                <input placeholder="End (or Present)" value={job.end || ""} onChange={(e) => setExpItem(i, "end", e.target.value)}
                                  className="rounded-md border border-[#D6DCE3] px-2 py-1.5 text-sm" />
                              </div>
                            </div>
                            <textarea rows={3} placeholder="Bullets (one per line — outcome-focused)"
                              value={(job.bullets || []).join("\n")} onChange={(e) => setExpBullets(i, e.target.value)}
                              className="w-full rounded-md border border-[#D6DCE3] px-2 py-1.5 text-sm" />
                          </div>
                        ))}
                        <button onClick={addExp} className="text-xs inline-flex items-center gap-1 text-[#0D2F4D] hover:text-[#C6D21F]" data-testid="editor-add-exp">
                          <Plus size={12} /> Add role
                        </button>
                      </div>
                    </Field>

                    <Field label="Education">
                      <div className="space-y-2">
                        {(draft.education || []).map((e, i) => (
                          <div key={i} className="grid grid-cols-[1fr_1fr_80px_28px] gap-2 items-center">
                            <input placeholder="Qualification" value={e.qualification || ""}
                              onChange={(ev) => setEduItem(i, "qualification", ev.target.value)}
                              className="rounded-md border border-[#D6DCE3] px-2 py-1.5 text-sm" />
                            <input placeholder="Institution" value={e.institution || ""}
                              onChange={(ev) => setEduItem(i, "institution", ev.target.value)}
                              className="rounded-md border border-[#D6DCE3] px-2 py-1.5 text-sm" />
                            <input placeholder="Year" value={e.year || ""}
                              onChange={(ev) => setEduItem(i, "year", ev.target.value)}
                              className="rounded-md border border-[#D6DCE3] px-2 py-1.5 text-sm" />
                            <button onClick={() => removeEdu(i)} className="text-[#B91C1C] hover:opacity-80"><Trash2 size={12} /></button>
                          </div>
                        ))}
                        <button onClick={addEdu} className="text-xs inline-flex items-center gap-1 text-[#0D2F4D] hover:text-[#C6D21F]">
                          <Plus size={12} /> Add education
                        </button>
                      </div>
                    </Field>

                    <Field label="Certifications (one per line)">
                      <textarea rows={2} value={(draft.certifications || []).join("\n")}
                        onChange={(e) => setDraftField("certifications", e.target.value.split("\n").filter((s) => s.trim()))}
                        className="w-full rounded-lg border border-[#D6DCE3] px-3 py-2 text-sm focus:border-[#0D2F4D] outline-none" />
                    </Field>

                    {draft.notes_for_polish && (
                      <div className="rounded-lg bg-[#FDF4E4] border border-[#E9D89E] p-3 text-xs text-[#8B6914]">
                        <b>Claude's note:</b> {draft.notes_for_polish}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-[#ECF0F4]">
                      <button onClick={saveDraft} disabled={saving || !dirty}
                        data-testid="editor-save"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-[#0D2F4D] text-[#0D2F4D] text-sm font-bold hover:bg-[#F8F9FB] disabled:opacity-50">
                        {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save draft
                      </button>
                      <button onClick={approveAndSend} disabled={sending || detail.stage === "approved_sent"}
                        data-testid="editor-approve-send"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0D2F4D] text-white text-sm font-bold hover:bg-[#1a4063] disabled:opacity-50">
                        {sending ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
                        {detail.stage === "approved_sent" ? "Already sent" : "Approve & send"}
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          if (detail.manual_cv_sent) { toast.info("Already marked as manually sent."); return; }
                          const note = window.prompt(
                            'Optional note about why this was sent manually (e.g. "emailed straight to Sarah after her call"). Leave blank to skip.',
                            "",
                          );
                          if (note === null) return;
                          try {
                            await axios.post(
                              `${API}/admin/resume-reviews/${detail.id}/manual-sent`,
                              { note, admin_email: (typeof window !== "undefined" && window.localStorage?.getItem("adminEmail")) || "" },
                              { headers: { "X-Admin-Password": pw } },
                            );
                            toast.success("Marked as manually sent.");
                            await openDetail(detail.id); load();
                          } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
                        }}
                        disabled={detail.manual_cv_sent}
                        data-testid="editor-mark-manual-sent"
                        title="Tick this if you emailed the CV yourself instead of using the automated flow."
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-colors ${
                          detail.manual_cv_sent
                            ? "bg-[#EFF6FF] text-[#0D2F4D] border border-[#93C5FD] cursor-not-allowed"
                            : "bg-white text-[#0D2F4D] border border-[#0D2F4D]/30 hover:border-[#0D2F4D]"
                        }`}
                      >
                        {detail.manual_cv_sent ? "✓ Manually sent" : "Mark manually sent"}
                      </button>
                      <span className="text-[10px] text-[#6F8FA8]">
                        Approve triggers PDF build + email delivery ({dirty ? "uses your unsaved edits" : "uses saved draft"}).
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="bg-[#FDF4E4] text-[#8B6914] rounded-lg p-4 text-sm inline-flex items-center gap-2">
                    <Clock size={14} /> Client hasn't submitted intake yet — no draft to review.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Manual "Send CV Builder link" modal — Shadcn Dialog */}
      <Dialog open={showLinkModal} onOpenChange={(o) => { if (!o) resetLinkModal(); else setShowLinkModal(true); }}>
        <DialogContent className="max-w-lg" data-testid="manual-link-modal">
          {!linkResult ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl font-extrabold text-[#0D2F4D] flex items-center gap-2">
                  <Link2 size={18} /> Send CV Builder link
                </DialogTitle>
                <DialogDescription className="text-sm text-[#6F8FA8] leading-relaxed">
                  Mints a fresh CV Builder intake link for someone you're onboarding manually (invoiced, comped, or added-on).
                  We'll also email them the link automatically. If this email already has an in-progress review, we'll reuse it.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <div>
                  <Label className="text-[10px] uppercase tracking-widest font-bold text-[#0D2F4D]">Email <span className="text-red-500">*</span></Label>
                  <Input type="email" value={linkForm.email} onChange={(e) => setLinkForm({ ...linkForm, email: e.target.value })}
                    data-testid="manual-link-email"
                    placeholder="cara@example.com"
                    className="mt-1 border-[#D6DCE3] focus-visible:ring-[#0D2F4D]" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest font-bold text-[#0D2F4D]">First name</Label>
                    <Input value={linkForm.first_name} onChange={(e) => setLinkForm({ ...linkForm, first_name: e.target.value })}
                      placeholder="Cara" className="mt-1 border-[#D6DCE3] focus-visible:ring-[#0D2F4D]" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest font-bold text-[#0D2F4D]">Last name</Label>
                    <Input value={linkForm.last_name} onChange={(e) => setLinkForm({ ...linkForm, last_name: e.target.value })}
                      placeholder="Latham" className="mt-1 border-[#D6DCE3] focus-visible:ring-[#0D2F4D]" />
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest font-bold text-[#0D2F4D]">Internal note (optional)</Label>
                  <Input value={linkForm.note} onChange={(e) => setLinkForm({ ...linkForm, note: e.target.value })}
                    placeholder="Add-on to Settlement package, invoiced 4 Aug"
                    className="mt-1 border-[#D6DCE3] focus-visible:ring-[#0D2F4D]" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={resetLinkModal} className="border-[#D6DCE3] text-[#0D2F4D]">
                  Cancel
                </Button>
                <Button onClick={submitManualLink} disabled={linkSubmitting || !linkForm.email}
                  data-testid="manual-link-submit"
                  className="bg-[#0D2F4D] text-white hover:bg-[#1a4063]">
                  {linkSubmitting ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />} Create link
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl font-extrabold text-[#0D2F4D] flex items-center gap-2">
                  <CheckCircle2 size={18} className="text-[#B0BC1A]" />
                  {linkResult.reused_existing ? "Existing link found" : "Link ready to share"}
                </DialogTitle>
                <DialogDescription className="text-sm text-[#6F8FA8]">
                  For <span className="text-[#0D2F4D] font-bold">{linkResult.client_email}</span>. We've also emailed them the intake link.
                </DialogDescription>
              </DialogHeader>
              <div className="rounded-lg bg-[#F8F9FB] p-3 break-all text-sm text-[#0D2F4D] font-mono" data-testid="manual-link-url">
                {linkResult.intake_url}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={() => copyText(linkResult.intake_url, "Link copied.")}
                  data-testid="manual-link-copy"
                  className="border-[#D6DCE3] text-[#0D2F4D]">
                  <Copy size={12} /> Copy link
                </Button>
                <Button variant="outline" size="sm" asChild className="border-[#D6DCE3] text-[#0D2F4D]">
                  <a href={linkResult.intake_url} target="_blank" rel="noreferrer">
                    <ExternalLink size={12} /> Open intake
                  </a>
                </Button>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest font-bold text-[#0D2F4D]">Message to send client</Label>
                <Textarea readOnly rows={5} value={linkResult.share_message}
                  data-testid="manual-link-message"
                  className="mt-1 bg-[#F8F9FB] border-[#D6DCE3]" />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => copyText(linkResult.share_message, "Message copied.")} className="border-[#D6DCE3] text-[#0D2F4D]">
                  <Copy size={12} /> Copy message
                </Button>
                <Button onClick={resetLinkModal} className="bg-[#0D2F4D] text-white hover:bg-[#1a4063]">
                  Done
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <label className="block text-[10px] uppercase tracking-widest font-bold text-[#0D2F4D]">{label}</label>
      {children}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg bg-[#F8F9FB] p-2">
      <p className="text-[10px] uppercase tracking-widest text-[#6F8FA8]">{label}</p>
      <p className="text-[#0D2F4D] font-semibold text-sm">{value}</p>
    </div>
  );
}

function MetricPill({ label, value, testid }) {
  return (
    <div className="rounded-md bg-white/70 border border-[#C6D21F]/50 px-2 py-1.5" data-testid={testid}>
      <p className="text-[9px] uppercase tracking-widest text-[#6F8FA8]">{label}</p>
      <p className="text-[#0D2F4D] font-bold text-sm leading-tight">{value}</p>
    </div>
  );
}

